import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import { Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { getIngestArchiveImpact } from 'api/jobs';
import { compact } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import React from 'react';

import { useConfirmation } from 'components/modals/ConfirmationContext';
import { IngestArchiveImpactResponse, Job, JobArchiveOperation, JobStatus, JobType } from 'interfaces/job';
import { Permission } from 'interfaces/permissionOption';
import { useAppSelector } from 'redux/hooks';
import { useJobArchiveMutation, usePendingJobArchiveOperation } from 'utils/queryHooks/jobs/useJobArchive';
import { usePermissions } from 'utils/usePermissions';
import { useCurrentLabId } from 'utils/useCurrentLab';

interface JobArchiveControlProps {
  job: Pick<Job, 'name' | 'status' | 'userId' | 'deletedAt'> & {
    id: string;
    type: Job['type'] | null | undefined;
  };
  labeled?: boolean;
  /** Dense timeline rows use the shared contained secondary archive treatment. */
  contained?: boolean;
  /** Called once an archive request succeeded, e.g. to close the job details. */
  onArchived?: () => void;
  /** Extra consequences to state in the confirmation, per operation. */
  confirmationNotes?: Partial<Record<JobArchiveOperation, string>>;
  /** Replaces the default action label when the surrounding screen has a more specific consequence. */
  actionLabels?: Partial<Record<JobArchiveOperation, string>>;
  /** Replaces the default confirmation body when the surrounding screen must state a specific consequence. */
  confirmationText?: Partial<Record<JobArchiveOperation, string>>;
}

const terminalJobStatuses = new Set<string>([
  JobStatus.Completed,
  JobStatus.Failed,
  JobStatus.FailedToStart,
  JobStatus.Cancelled,
]);

const operationConfig = {
  archive: {
    label: 'Archive job',
    Icon: ArchiveOutlinedIcon,
  },
  restore: {
    label: 'Restore job',
    Icon: UnarchiveOutlinedIcon,
  },
} as const;

export const ingestRestoreConsequences =
  'If this ingest created a slide, restoring it makes the slide available for case assignment unless hidden. It does not restore removed case assignments or archived cases.';

export const getIngestArchiveConsequences = (impact: IngestArchiveImpactResponse) => {
  const fileConsequence = 'The uploaded file remains; its hidden state is unchanged.';
  if (!impact.slideId) {
    return `This ingest did not create a slide. No case assignments will change. ${fileConsequence}`;
  }
  const assignmentConsequence = impact.hasOtherUnarchivedIngest
    ? 'Another unarchived ingest uses this slide. Its case assignments will remain unchanged.'
    : 'Rejecting this ingest removes its case assignments. Cases left without slides will be archived.';
  return `${assignmentConsequence} The slide record remains. ${fileConsequence}`;
};

export const useJobArchiveAction = (job: Omit<JobArchiveControlProps['job'], 'type'>) => {
  const currentUserId = useAppSelector((state) => state.auth.profile?.userId);
  const { hasPermission, isLoading: isLoadingPermissions } = usePermissions();
  const canManageAllJobs = hasPermission(Permission.ArchiveRestoreAllUsersJobs);
  const canManageOwnJob =
    hasPermission(Permission.ArchiveRestoreOwnJobs) && Boolean(currentUserId) && job.userId === currentUserId;
  const isTerminal = Boolean(job.status && terminalJobStatuses.has(job.status));
  const pendingOperation = usePendingJobArchiveOperation(job.id);
  const operation: JobArchiveOperation = pendingOperation || (job.deletedAt ? 'restore' : 'archive');

  return {
    isAvailable: !isLoadingPermissions && isTerminal && (canManageAllJobs || canManageOwnJob),
    operation,
    ...operationConfig[operation],
    isPending: Boolean(pendingOperation),
  };
};

export const JobArchiveControl: React.FC<JobArchiveControlProps> = ({
  job,
  labeled = false,
  contained = false,
  confirmationNotes,
  confirmationText,
  actionLabels,
  onArchived,
}) => {
  const confirm = useConfirmation();
  const queryClient = useQueryClient();
  const { labId } = useCurrentLabId();
  const [isCheckingImpact, setIsCheckingImpact] = React.useState(false);
  const checkingImpact = React.useRef(false);
  const currentScope = React.useRef({ jobId: job.id, labId });
  currentScope.current = { jobId: job.id, labId };
  const archiveMutation = useJobArchiveMutation();
  const { isAvailable, operation, Icon, label, isPending } = useJobArchiveAction(job);
  const actionLabel = actionLabels?.[operation] || label;
  const isBusy = isPending || isCheckingImpact;

  if (!isAvailable) {
    return null;
  }

  const requestOperation = async () => {
    if (checkingImpact.current || archiveMutation.isPendingForJob(job.id)) {
      return;
    }
    if (job.type === undefined) {
      enqueueSnackbar('Job type is unavailable. The archive action cannot be confirmed.', { variant: 'error' });
      return;
    }

    let ingestConsequences: string | undefined;
    if (job.type === JobType.Ingest) {
      if (operation === 'restore') {
        ingestConsequences = confirmationText?.restore ? undefined : ingestRestoreConsequences;
      } else {
        checkingImpact.current = true;
        setIsCheckingImpact(true);
        try {
          const impact = await queryClient.fetchQuery({
            queryKey: ['ingestArchiveImpact', labId, job.id],
            queryFn: () => getIngestArchiveImpact(labId, job.id),
            staleTime: 0,
            cacheTime: 0,
            retry: false,
          });
          ingestConsequences = getIngestArchiveConsequences(impact);
        } catch {
          enqueueSnackbar('Ingest archive consequences could not be checked. Try again.', { variant: 'error' });
          return;
        } finally {
          checkingImpact.current = false;
          setIsCheckingImpact(false);
        }
      }
    }
    if (currentScope.current.jobId !== job.id || currentScope.current.labId !== labId) return;
    const displayJob = job.name || job.id;
    const defaultConfirmationText =
      operation === 'archive'
        ? `Archive ${displayJob}? The job will leave the Jobs view. Its history and directly produced results remain recoverable.`
        : `Restore ${displayJob}? The job will return to the Jobs view with eligible directly produced results.`;
    const confirmed = await confirm({
      title: `${actionLabel}?`,
      text: compact([
        confirmationText?.[operation] ?? defaultConfirmationText,
        ingestConsequences,
        confirmationNotes?.[operation],
      ]).join(' '),
      confirmButtonProps: {
        title: actionLabels?.[operation] || operationConfig[operation].label.replace(' job', ''),
        color: contained ? 'secondary' : operation === 'archive' ? 'warning' : 'primary',
      },
    });

    if (
      confirmed &&
      currentScope.current.jobId === job.id &&
      currentScope.current.labId === labId &&
      !archiveMutation.isPendingForJob(job.id)
    ) {
      archiveMutation.mutate(
        { jobId: job.id, operation },
        {
          onSuccess: () => {
            if (operation === 'archive') {
              onArchived?.();
            }
          },
        }
      );
    }
  };

  if (labeled) {
    return (
      <Button
        size="small"
        variant={contained ? 'contained' : 'outlined'}
        color={contained ? 'secondary' : operation === 'archive' ? 'warning' : 'primary'}
        startIcon={isBusy ? <CircularProgress size={16} /> : <Icon />}
        disabled={isBusy}
        aria-busy={isCheckingImpact}
        onClick={(event) => {
          event.stopPropagation();
          void requestOperation();
        }}
        sx={{ textTransform: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        {isPending ? `${operation === 'archive' ? 'Archiving' : 'Restoring'}...` : actionLabel}
      </Button>
    );
  }

  return (
    <Tooltip title={actionLabel}>
      <span>
        <IconButton
          size="small"
          aria-label={actionLabel}
          disabled={isBusy}
          aria-busy={isCheckingImpact}
          onClick={(event) => {
            event.stopPropagation();
            void requestOperation();
          }}
        >
          {isBusy ? <CircularProgress size={20} /> : <Icon />}
        </IconButton>
      </span>
    </Tooltip>
  );
};
